import { langToUrl } from './urls';
import { FIRST_FULL_TIME_MIGRATION, CRM_SUITE_ENTERPRISE_BUNDLE, CRM_SUITE_PROFESSIONAL_BUNDLE, CRM_SUITE_STARTER_BUNDLE } from 'self-service-api/constants/ApiNames';
import * as CtaTypes from '../constants/CtaTypes';
import * as Languages from '../constants/Languages';
import { BUNDLE } from '../constants/NonMerchandiseIdProducts'; // (may 8, 2019) cta documentation:
// https://docs.google.com/spreadsheets/d/1BHY9NGG6e41cna8s31RnxY1Sn0I7kNVBzzwuAxVd2D0/edit?ts=5cd1f219#gid=1017927284

const apiNameToSignupStringMap = {
  [FIRST_FULL_TIME_MIGRATION]: 'cmsmigrations',
  [CRM_SUITE_STARTER_BUNDLE]: 'crm-suite-starter',
  [CRM_SUITE_PROFESSIONAL_BUNDLE]: 'crm-suite-professional',
  [CRM_SUITE_ENTERPRISE_BUNDLE]: 'crm-suite-enterprise'
}; // hubs_signup-cta can be derived from sourceId and the
// nonMerchandiseIdProduct so we centralize it here
// rather than having each purchase motion explicitly define it

export const getHubsSignupCTA = ({
  ctaType,
  sourceId,
  nonMerchandiseIdProduct,
  productLine,
  productLevel,
  apiName
}) => {
  let resolvedProductIdentifier;
  let resolvedSourceIdentifier;

  if (!nonMerchandiseIdProduct && !productLine && !(apiName && apiName in apiNameToSignupStringMap)) {
    throw new Error('need one of (nonMerchandiseIdProduct) or (productLine) or (apiName that is present in apiNameToSignupStringMap)');
  }

  if (ctaType === CtaTypes.BUY || ctaType === CtaTypes.LEARN_MORE || ctaType === CtaTypes.TRIAL) {
    const sourceParts = sourceId.split('-');
    resolvedSourceIdentifier = `${sourceParts[0]}-${ctaType}${sourceParts[1]}`;
  } else {
    resolvedSourceIdentifier = sourceId;
  }

  if (apiName && apiName in apiNameToSignupStringMap) {
    resolvedProductIdentifier = apiNameToSignupStringMap[apiName];
  } else if (productLine) {
    resolvedProductIdentifier = `${productLine.toLowerCase()}${productLevel ? productLevel.toLowerCase() : ''}`;
  } else {
    resolvedProductIdentifier = nonMerchandiseIdProduct;
  }

  return `${resolvedProductIdentifier}-${resolvedSourceIdentifier}`;
}; // hubs_signup-url is added as a query param to any CTAs on the public pricing page  for the the marketing
// team's GA  tracking. New CTAs can be added just in a manner consistent to other ones, but we should be
// mindful of editing existing ones as it could break tracking and we should consult with web strategy first

export const getHubsSignupUrl = ({
  nonMerchandiseIdProduct,
  baseUrl
}) => {
  baseUrl = baseUrl || `${window.location.hostname}${window.location.pathname}`; // Strip query params from this URL

  let url = encodeURI(baseUrl.split('?')[0]);

  if (nonMerchandiseIdProduct && nonMerchandiseIdProduct === BUNDLE) {
    // Pass whole url (including query params) for bundles
    url = encodeURI(baseUrl);
  }

  return url;
}; // This function exists to simply wrap classNames so we are more aware that
// they are necessary for Google Analytics tracking and don't remove them accidentally

export const getGAClassName = gaTrackingClassname => gaTrackingClassname; // need to keep a check on this - not sure if will be accurate for all links

export const getPublicSiteDomain = () => {
  const langMap = {
    [Languages.EN]: 'https://www.hubspot.com',
    [Languages.DE]: 'https://www.hubspot.de',
    [Languages.ES]: 'https://www.hubspot.es',
    [Languages.PT]: 'https://br.hubspot.com',
    [Languages.FR]: 'https://www.hubspot.fr',
    [Languages.JA]: 'https://www.hubspot.jp'
  };
  return langToUrl(langMap);
};
export const getStartedCmsUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/marketing/content-management-system/get-started`;
};
export const getMarketingUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/marketing`;
};
export const getSalesUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/sales`;
};
export const getServiceUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/service`;
};
export const getCMSUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/cms`;
};
export const getCMSOnboardingUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/services/onboarding/cms`;
};
export const getOperationsUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/operations`;
};
export const getOnboardingServicesUrlPublic = isForStartups => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/services/onboarding/${isForStartups ? 'startup' : ''}`;
};
export const getPaymentsUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/payments`;
};
export const getStartupsUrlPublic = sourceId => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/startups?source=dot-com-pricing&hubs_content=${window.location.href.split('?')[0]}&hubs_content-cta=${getHubsSignupCTA({
    ctaType: 'learnmore',
    sourceId,
    nonMerchandiseIdProduct: 'hsfs'
  })}`;
};
export const getNonprofitsUrlPublic = sourceId => {
  // Localized url does not exist yet
  return `https://www.hubspot.com/nonprofits?source=dot-com-pricing&hubs_content=${window.location.href.split('?')[0]}&hubs_content-cta=${getHubsSignupCTA({
    ctaType: 'learnmore',
    sourceId,
    nonMerchandiseIdProduct: 'hsfn'
  })}`;
};
export const getProductAndServicesUrlPublic = () => {
  const langMap = {
    [Languages.EN]: 'https://legal.hubspot.com/hubspot-product-and-services-catalog',
    [Languages.DE]: 'https://legal.hubspot.com/de/hubspot-product-and-services-catalog',
    [Languages.ES]: 'https://legal.hubspot.com/es/hubspot-product-and-services-catalog',
    [Languages.PT]: 'https://legal.hubspot.com/br/hubspot-product-and-services-catalog',
    [Languages.FR]: 'https://legal.hubspot.com/fr/hubspot-product-and-services-catalog',
    [Languages.JA]: 'https://legal.hubspot.com/jp/hubspot-product-and-services-catalog'
  };
  return langToUrl(langMap);
};
export const getKnowledgeBaseUrlPublic = () => {
  const langMap = {
    [Languages.EN]: 'https://knowledge.hubspot.com',
    [Languages.DE]: 'https://knowledge.hubspot.com/de',
    [Languages.ES]: 'https://knowledge.hubspot.com/es',
    [Languages.PT]: 'https://knowledge.hubspot.com/pt',
    [Languages.FR]: 'https://knowledge.hubspot.com/fr',
    [Languages.JA]: 'https://knowledge.hubspot.com/jp'
  };
  return langToUrl(langMap);
};
export const getCommunityUrlPublic = () => {
  const langMap = {
    [Languages.EN]: 'https://community.hubspot.com',
    [Languages.DE]: 'https://community.hubspot.com/?profile.language=de',
    [Languages.ES]: 'https://community.hubspot.com/?profile.language=es',
    [Languages.PT]: 'https://community.hubspot.com/?profile.language=pt-br',
    [Languages.FR]: 'https://community.hubspot.com/?profile.language=fr',
    [Languages.JA]: 'https://community.hubspot.com/?profile.language=ja'
  };
  return langToUrl(langMap);
};
export const getAcademyUrlPublic = () => {
  const langMap = {
    [Languages.EN]: 'https://academy.hubspot.com',
    [Languages.DE]: 'https://academy.hubspot.de',
    [Languages.ES]: 'https://academy.hubspot.com/es',
    [Languages.PT]: 'https://academy.hubspot.com/pt',
    [Languages.FR]: 'https://academy.hubspot.fr',
    [Languages.JA]: 'https://academy.hubspot.jp'
  };
  return langToUrl(langMap);
};
export const getLanguagesUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/account/hubspot-language-offerings`;
};
export const getKBPaymentsUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/payments/payments-frequently-asked-questions`;
};
export const PAYMENTS_LANDING_PAGE_URL = 'https://www.hubspot.com/products/payments';
export const getTtsUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://offers.hubspot.com/contact-sales',
    [Languages.DE]: 'https://offers.hubspot.de/contact-sales',
    [Languages.ES]: 'https://offers.hubspot.es/contact-sales',
    [Languages.PT]: 'https://br.hubspot.com/ofertas/contact-sales',
    [Languages.FR]: 'https://offers.hubspot.fr/contact-sales',
    [Languages.JA]: 'https://offers.hubspot.jp/contact-sales'
  };
  return langToUrl(langMap);
};
export const getOperationsSupportedApplicationsUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://ecosystem.hubspot.com/marketplace/apps/apps-for-operations-teams',
    [Languages.DE]: 'https://ecosystem.hubspot.com/de/marketplace/apps/apps-for-operations-teams',
    [Languages.ES]: 'https://ecosystem.hubspot.com/es/marketplace/apps/apps-for-operations-teams',
    [Languages.PT]: 'https://ecosystem.hubspot.com/pt/marketplace/apps/apps-for-operations-teams',
    [Languages.FR]: 'https://ecosystem.hubspot.com/fr/marketplace/apps/apps-for-operations-teams',
    [Languages.JA]: 'https://ecosystem.hubspot.com/ja/marketplace/apps/apps-for-operations-teams'
  };
  return langToUrl(langMap);
};
export const getPartnerProgramUrl = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/partners/solutions`;
};